import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Plus, Sparkles, Download, Printer,
  Share2, Copy, Check, Ban, Camera, Palette,
} from "lucide-react";
import client, { fetchProfilePhotoUrl } from "../lib/api";

/**
 * Resume. Owner-only surface. Assembles from the person's own artefacts.
 * The buyer wall guarantees no buyer can see anything on this route.
 */
export default function ResumeScreen({ product }) {
  const base = product === "grow" ? "/grow" : "";
  const [resume, setResume] = useState({ headline: "", summary: "" });
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [skills, setSkills] = useState([]);
  const [newExp, setNewExp] = useState({ title: "", organisation: "", bullets: "" });
  const [newSkill, setNewSkill] = useState({ name: "", evidence: "" });
  const [busy, setBusy] = useState(false);
  const [savedNote, setSavedNote] = useState("");
  const [name, setName] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [shares, setShares] = useState([]);
  const [copied, setCopied] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("klareet.resume.theme") || "modern"; } catch (_e) { return "modern"; }
  });

  const THEME_ACCENT = { modern: "#0f766e", classic: "#333333", minimal: "#111111" };
  const printAccent = THEME_ACCENT[theme] || "#0f766e";

  const pickTheme = (t) => {
    setTheme(t);
    try { localStorage.setItem("klareet.resume.theme", t); } catch (_e) { /* ignore */ }
  };

  const load = async () => {
    try {
      const [r, e, ed, s] = await Promise.all([
        client.get("/resume"),
        client.get("/resume/experience"),
        client.get("/resume/education"),
        client.get("/resume/skill"),
      ]);
      let headline = r.data?.headline || "";
      let summary = r.data?.summary || "";
      // Auto-fill from the person's own read on first open, when both
      // fields are still empty, then persist quietly so the PDF and
      // share carry it. Never clobbers anything they wrote.
      if (!headline && !summary) {
        let rep = null;
        try { rep = JSON.parse(localStorage.getItem("klareet.report") || "null"); } catch (_e) { /* ignore */ }
        const arch = rep?.archetype?.primary;
        const honest = (rep?.honest_line || "").trim();
        if (arch?.name || honest) {
          headline = arch?.name ? String(arch.name) : "";
          summary = honest;
          try { await client.patch("/resume", { headline, summary }); } catch (_e) { /* ignore */ }
          setSavedNote("Filled from your read. Edit to make it sound like you.");
          setTimeout(() => setSavedNote(""), 3200);
        }
      }
      setResume({ headline, summary });
      setExperiences(e.data?.items || []);
      setEducations(ed.data?.items || []);
      setSkills(s.data?.items || []);
    } catch (_e) { /* first time */ }
  };

  const loadShares = () => {
    client.get("/resume/shares").then((r) => setShares(r.data?.items || [])).catch(() => setShares([]));
  };

  useEffect(() => {
    load();
    loadShares();
    client.get("/consent/status").then((r) => {
      const n = (r?.data?.person?.display_name || "").trim();
      if (n) setName(n);
    }).catch(() => {});
    let live = true;
    fetchProfilePhotoUrl().then((u) => { if (live) setPhotoUrl(u); });
    return () => {
      live = false;
      setPhotoUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
    };
  }, []);

  const saveResume = async () => {
    setBusy(true);
    try {
      await client.patch("/resume", { headline: resume.headline, summary: resume.summary });
      setSavedNote("Saved.");
      setTimeout(() => setSavedNote(""), 1400);
    } catch (_e) {
      setSavedNote("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  // Autofill headline and summary from the person's own read, but only
  // where the field is empty so nothing they wrote is clobbered. They
  // review and save; nothing is stored until they do.
  const autofill = () => {
    let rep = null;
    try { rep = JSON.parse(localStorage.getItem("klareet.report") || "null"); } catch (_e) { /* ignore */ }
    const arch = rep?.archetype?.primary;
    const honest = (rep?.honest_line || "").trim();
    if (!arch && !honest) {
      setSavedNote("Do a conversation first, then I can fill this from your read.");
      setTimeout(() => setSavedNote(""), 2800);
      return;
    }
    setResume((prev) => ({
      headline: prev.headline || (arch?.name ? String(arch.name) : ""),
      summary: prev.summary || honest,
    }));
    setSavedNote("Filled from your read. Make it sound like you, then save.");
    setTimeout(() => setSavedNote(""), 3200);
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await client.get("/resume/pdf", { responseType: "blob", params: { theme } });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "resume").replace(/\s+/g, "_").toLowerCase()}_resume.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (_e) {
      setSavedNote("Download failed. Try again in a moment.");
      setTimeout(() => setSavedNote(""), 2600);
    } finally {
      setPdfBusy(false);
    }
  };

  const printResume = () => window.print();

  const createShare = async () => {
    try {
      await client.post("/resume/shares", { product });
      loadShares();
    } catch (_e) { /* ignore */ }
  };

  const revokeShare = async (slug) => {
    try {
      await client.post(`/resume/shares/${slug}/revoke`);
      loadShares();
    } catch (_e) { /* ignore */ }
  };

  const copyLink = (path) => {
    const full = `${window.location.origin}${path}`;
    navigator.clipboard?.writeText(full).then(() => {
      setCopied(path);
      setTimeout(() => setCopied(""), 1800);
    }).catch(() => {});
  };

  const addExperience = async () => {
    if (!newExp.title.trim() || !newExp.organisation.trim()) return;
    const bullets = newExp.bullets.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 6);
    try {
      await client.post("/resume/experience", {
        title: newExp.title.trim(),
        organisation: newExp.organisation.trim(),
        bullets,
        tags: [],
        is_current: true,
      });
      setNewExp({ title: "", organisation: "", bullets: "" });
      load();
    } catch (_e) { /* ignore */ }
  };

  const addSkill = async () => {
    if (!newSkill.name.trim()) return;
    try {
      await client.post("/resume/skill", { name: newSkill.name.trim(), evidence: newSkill.evidence.trim() || null });
      setNewSkill({ name: "", evidence: "" });
      load();
    } catch (_e) { /* ignore */ }
  };

  const buildOnePager = async () => {
    try {
      await client.post("/resume/one-pager");
      setSavedNote("One pager built.");
      setTimeout(() => setSavedNote(""), 1800);
    } catch (_e) { /* ignore */ }
  };

  const initial = (name || "Y").slice(0, 1).toUpperCase();
  const activeShares = shares.filter((s) => s.is_active);

  return (
    <div className="max-w-4xl mx-auto px-5 pt-8 pb-24">
      <div className="k-fade-in">
        <div className="k-label mb-2">my one-pager</div>
        <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="resume-title">
          My One-Pager. The one page version of you.
        </h1>
        <p className="k-hero-sub mb-6">Private by default. You choose when and how to share. This is the one place Klareet speaks recruiter: keep the language professional here, because a hiring system reads it before a human does.</p>
        <AtsCheckCard />
      </div>

      {/* Header: photo + who this is, plus the export actions. */}
      <div className="k-card mb-6" data-testid="resume-header">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {photoUrl ? (
              <img src={photoUrl} alt={name || "you"} className="w-14 h-14 rounded-2xl object-cover shrink-0" data-testid="resume-photo" />
            ) : (
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-semibold shrink-0" style={{ background: "hsl(var(--klareet-ink))", color: "white" }} data-testid="resume-photo-fallback">
                {initial}
              </div>
            )}
            <div className="min-w-0">
              <div className="klareet-serif" style={{ fontSize: "1.25rem", color: "hsl(var(--klareet-ink))" }}>{name || "Your name"}</div>
              {!photoUrl && (
                <Link to={`${base}/account`} className="k-quiet !text-xs inline-flex items-center gap-1 hover:underline" data-testid="resume-add-photo-link">
                  <Camera size={12} /> add a photo in your profile
                </Link>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={autofill} className="k-btn k-btn-ghost" data-testid="resume-autofill">
              <Sparkles size={14} /> Autofill from my read
            </button>
            <button onClick={downloadPdf} disabled={pdfBusy} className="k-btn k-btn-primary" data-testid="resume-download-pdf">
              <Download size={14} /> {pdfBusy ? "Preparing" : "Download PDF"}
            </button>
            <button onClick={printResume} className="k-btn k-btn-outline" data-testid="resume-print">
              <Printer size={14} /> Print
            </button>
          </div>
        </div>

        {/* Theme picker: a couple of clean looks for the download. */}
        <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-2" style={{ borderColor: "hsl(var(--klareet-line-soft))" }} data-testid="resume-theme-picker">
          <span className="k-label !mb-0 mr-1 inline-flex items-center gap-1.5"><Palette size={13} /> look</span>
          {[
            { key: "modern", label: "Modern" },
            { key: "classic", label: "Classic" },
            { key: "minimal", label: "Minimal" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => pickTheme(t.key)}
              className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={theme === t.key
                ? { background: "hsl(var(--klareet-ink))", color: "white" }
                : { background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-ink-soft))", border: "1px solid hsl(var(--klareet-line-soft))" }}
              data-testid={`resume-theme-${t.key}`}
              aria-pressed={theme === t.key}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="k-card mb-6">
        <div className="k-label mb-3">headline and summary</div>
        <label className="k-label !text-[10px]">Headline</label>
        <input
          className="k-input mt-1 mb-3"
          value={resume.headline}
          onChange={(e) => setResume({ ...resume, headline: e.target.value })}
          placeholder="one line that says what you do"
          data-testid="resume-headline"
        />
        <label className="k-label !text-[10px]">Summary</label>
        <textarea
          className="k-input mt-1 klareet-serif"
          rows={3}
          value={resume.summary}
          onChange={(e) => setResume({ ...resume, summary: e.target.value })}
          placeholder="two or three sentences in your own words"
          data-testid="resume-summary"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="k-quiet !text-xs">{savedNote}</span>
          <button onClick={saveResume} disabled={busy} className="k-btn k-btn-primary" data-testid="resume-save">
            {busy ? "Saving." : "Save"}
          </button>
        </div>
      </div>

      <div className="k-card mb-6" data-testid="experience-section">
        <div className="flex items-center justify-between mb-3">
          <div className="k-label">experience</div>
          <span className="k-quiet !text-xs">{experiences.length} entries</span>
        </div>
        {experiences.length === 0 ? (
          <div className="k-quiet" data-testid="experience-empty">Add your first experience below.</div>
        ) : (
          <ul className="space-y-3">
            {experiences.map((e) => (
              <li key={e.id} className="p-3 rounded-lg" style={{ background: "hsl(var(--klareet-canvas-2))" }} data-testid={`exp-${e.id}`}>
                <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{e.title}</div>
                <div className="k-quiet !text-xs">{e.organisation}</div>
                {Array.isArray(e.bullets) && e.bullets.length > 0 && (
                  <ul className="mt-2 text-sm list-disc pl-5" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
                    {e.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-5 border-t pt-4" style={{ borderColor: "hsl(var(--klareet-line-soft))" }}>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="k-input" placeholder="role title" value={newExp.title} onChange={(e) => setNewExp({ ...newExp, title: e.target.value })} data-testid="new-exp-title" />
            <input className="k-input" placeholder="organisation" value={newExp.organisation} onChange={(e) => setNewExp({ ...newExp, organisation: e.target.value })} data-testid="new-exp-org" />
          </div>
          <textarea className="k-input mt-3" rows={3} placeholder="what you did (one bullet per line)" value={newExp.bullets} onChange={(e) => setNewExp({ ...newExp, bullets: e.target.value })} data-testid="new-exp-bullets" />
          <button onClick={addExperience} className="k-btn k-btn-primary mt-3" data-testid="add-exp">
            <Plus size={14} /> Add experience
          </button>
        </div>
      </div>

      <div className="k-card mb-6" data-testid="skills-section">
        <div className="k-label mb-3">skills</div>
        {skills.length === 0 ? (
          <div className="k-quiet mb-3" data-testid="skills-empty">No skills yet.</div>
        ) : (
          <ul className="flex flex-wrap gap-2 mb-3" data-testid="skills-list">
            {skills.map((s) => (
              <li key={s.id} className="k-chip" data-testid={`skill-${s.id}`}>{s.name}</li>
            ))}
          </ul>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="k-input" placeholder="skill name" value={newSkill.name} onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })} data-testid="new-skill-name" />
          <input className="k-input" placeholder="evidence, in your own words" value={newSkill.evidence} onChange={(e) => setNewSkill({ ...newSkill, evidence: e.target.value })} data-testid="new-skill-evidence" />
        </div>
        <button onClick={addSkill} className="k-btn k-btn-primary mt-3" data-testid="add-skill">
          <Plus size={14} /> Add skill
        </button>
      </div>

      {/* Share: mint a private link, copy it, revoke it. */}
      <div className="k-card mb-6" data-testid="resume-share-card">
        <div className="flex items-start gap-3">
          <Share2 size={20} style={{ color: "hsl(var(--klareet-teal))" }} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>Share a private link</div>
            <p className="text-sm mt-1" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
              Anyone with the link can read your one pager. No sign in for them. You can switch a link off any time.
            </p>
            <button onClick={createShare} className="k-btn k-btn-primary mt-3" data-testid="resume-create-share">
              <Plus size={14} /> Create a share link
            </button>

            {activeShares.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="resume-share-list">
                {activeShares.map((s) => (
                  <li key={s.slug} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: "hsl(var(--klareet-canvas-2))" }} data-testid={`share-${s.slug}`}>
                    <code className="text-xs truncate flex-1 klareet-mono" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{s.path}</code>
                    <span className="k-quiet !text-[10px] shrink-0">{s.view_count} {s.view_count === 1 ? "view" : "views"}</span>
                    <button onClick={() => copyLink(s.path)} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid={`share-copy-${s.slug}`}>
                      {copied === s.path ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                    <button onClick={() => revokeShare(s.slug)} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid={`share-revoke-${s.slug}`}>
                      <Ban size={12} /> Turn off
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="k-warm-card p-6" data-testid="onepager-card">
        <div className="flex items-start gap-3">
          <FileText size={20} style={{ color: "hsl(var(--klareet-amber))" }} className="mt-1" />
          <div className="flex-1">
            <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>Assemble a one pager</div>
            <p className="text-sm mt-1" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
              Compose a single-page draft from your own artefacts. Private by default. You choose when to share.
            </p>
            <button onClick={buildOnePager} className="k-btn k-btn-accent mt-3" data-testid="build-onepager">
              <Sparkles size={14} /> Build one pager
            </button>
          </div>
        </div>
      </div>

      {/* Print-only clean resume. Hidden on screen; the Print button and
          the browser's Save as PDF both render this block only. */}
      <div className="resume-print" data-testid="resume-print-block" aria-hidden="true">
        <h1 style={{ fontSize: "24pt", margin: 0 }}>{name || "Your name"}</h1>
        {resume.headline && <div style={{ color: printAccent, fontSize: "12pt", marginTop: 2 }}>{resume.headline}</div>}
        <hr style={{ border: "none", borderTop: "1px solid #d9dde3", margin: "8pt 0" }} />
        {resume.summary && (
          <>
            <h2 style={{ fontSize: "10pt", letterSpacing: "0.08em", color: "#52606d" }}>SUMMARY</h2>
            <p style={{ fontSize: "10.5pt", lineHeight: 1.4 }}>{resume.summary}</p>
          </>
        )}
        {experiences.length > 0 && (
          <>
            <h2 style={{ fontSize: "10pt", letterSpacing: "0.08em", color: "#52606d" }}>EXPERIENCE</h2>
            {experiences.map((e) => (
              <div key={e.id} style={{ marginBottom: "8pt" }}>
                <div style={{ fontWeight: 700, fontSize: "11pt" }}>{[e.title, e.organisation].filter(Boolean).join(" at ")}</div>
                {Array.isArray(e.bullets) && (
                  <ul style={{ margin: "2pt 0", paddingLeft: "16pt", fontSize: "10.5pt" }}>
                    {e.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </>
        )}
        {skills.length > 0 && (
          <>
            <h2 style={{ fontSize: "10pt", letterSpacing: "0.08em", color: "#52606d" }}>SKILLS</h2>
            <p style={{ fontSize: "10.5pt" }}>{skills.map((s) => s.name).join("  ·  ")}</p>
          </>
        )}
      </div>
    </div>
  );
}


/**
 * KLA-045: the ATS check. Runs the deterministic parseability grade
 * against what is saved right now and shows every check honestly as
 * N of M passed, never a percent, never a rank.
 */
function AtsCheckCard() {
  const [grade, setGrade] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await client.post("/resume/ats-grade", { target_keywords: [] });
      setGrade(data || null);
    } catch (_e) {
      setErr("The check did not run. Save your one-pager first, then try again.");
    } finally { setBusy(false); }
  };

  const checks = (grade?.checks || []).filter((c) => c.outcome !== "na");
  const summary = grade?.passed != null && grade?.total != null
    ? `${grade.passed} of ${grade.total} checks passed` : "";

  return (
    <div className="k-card mb-6 print:hidden" data-testid="ats-check-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="k-label mb-1">the machine reader test</div>
          <p className="k-quiet !text-sm" style={{ maxWidth: 480, lineHeight: 1.5 }}>
            Most companies run your one-pager through screening software before a person sees it. This check tells you, honestly, whether that software can read yours.
          </p>
        </div>
        <button onClick={run} disabled={busy} className="k-btn k-btn-primary !text-sm" data-testid="ats-check-run">
          {busy ? "Checking." : grade ? "Check again" : "Run the check"}
        </button>
      </div>
      {err && <div className="k-chip k-chip-warn mt-3" data-testid="ats-check-error">{err}</div>}
      {grade && (
        <div className="mt-4" data-testid="ats-check-result">
          {summary && <div className="k-chip k-chip-teal mb-3" data-testid="ats-check-summary"><Check size={12} /> {summary}</div>}
          <ul className="space-y-1.5">
            {checks.map((c, i) => {
              const okc = c.outcome === "pass";
              return (
                <li key={c.id || i} className="flex items-start gap-2 text-sm" data-testid={`ats-check-item-${i}`}>
                  <span className="mt-0.5 shrink-0" style={{ color: okc ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-amber))" }}>
                    {okc ? <Check size={14} /> : <Ban size={14} />}
                  </span>
                  <span style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.45 }}>
                    {c.label}{!okc && c.reason ? ` ${c.reason}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
